import React, { useState, useRef, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { resetFilterOption, updateLabels, updateListFilter, updateRangeFilter, updatePendingFilters } from "../../features/leadTableFiltersSlice"
import "../../css/quilityIcons.css"
import NumberRange from "../../utilities/helperComponents/NumberRange"
import CheckBoxList from "../../utilities/helperComponents/CheckBoxList"
import DateRangePicker from "../../utilities/helperComponents/DateRangePicker"
import { useSelector } from "react-redux"
import { filterOptions, usStateInvertedHash, usStatesHash } from "../../utilities/genericUtils"
import moment from "moment/moment"
function FilterSelector({ closeHandler }) {

    const { date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status } = useSelector((state) => state.leadTableFilter)
    const [selectedFilters, setSelectedFilters] = useState({
        date_assigned: date_assigned.active,
        lead_age: lead_age.active,
        state: state.active,
        last_contacted: last_contacted.active,
        never_contacted: never_contacted.active,
        amount_contacted: amount_contacted.active,
        tags: tags.active,
        lead_type: lead_type.active,
        lead_level: lead_level.active,
        lead_status: lead_status.active,
    })
    const [currentFilter, setCurrentFilter] = useState(filterOptions.NONE)

    const leadStatus = useSelector((state) => state.status.status)
    const agentTags = useSelector((state) => state.tags.tags)
    const leadTypes = useSelector((state) => state.leadType.leadType)
    const agentStates = useSelector((state) => state.states.states)
    const leadLevels = useSelector((state) => state.leadLevels.leadLevels)

    /**
     * @type {CheckBoxListData[]}
     */
    const stateListOptions = []

    for (let index = 0; index < agentStates.length; index++) {
        const {states: s} = agentStates[index];
        const stateLabel = usStatesHash[s] ?? usStatesHash[usStateInvertedHash[s]]

        if(!stateLabel){
            continue
        }

        const existingState = stateListOptions.find(e => e.label === stateLabel)

        if(existingState){
            existingState.value.push(s)
        } else {
            stateListOptions.push({
                label: stateLabel,
                identifier: stateLabel,
                value: [s]
            })
        }
    }

    const dispatch = useDispatch()

    const handleFilterOption = (field) => {
        setSelectedFilters(prev => ({
            ...prev,
            [field]: true
        }))

       
        if (field === 'never_contacted') {
            dispatch(updateRangeFilter({ field: 'never_contacted', start: 0, end: 0 }))
            dispatch(updateLabels({ field: 'never_contacted', label: 'Not Dialed'}))
            dispatch(updatePendingFilters(true))
            setCurrentFilter(filterOptions.NONE)
            return
        }

        const filterKey = field.toUpperCase()
        setCurrentFilter(filterOptions[filterKey])

        if (filterKey !== filterOptions.NONE) {
            setMaxHeight(502)
            setOverflow(null)
        }
    }

    const [maxHeight, setMaxHeight] = useState(414)
    const [overflow, setOverflow] = useState('scroll')

    useEffect(() => {
        setSelectedFilters({
            date_assigned: date_assigned.active,
            lead_age: lead_age.active,
            state: state.active,
            last_contacted: last_contacted.active,
            never_contacted: never_contacted.active,
            amount_contacted: amount_contacted.active,
            lead_tags: tags.active,
            lead_type: lead_type.active,
            lead_level: lead_level.active,
            lead_status: lead_status.active,
        })
    }, [date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status])

    useEffect(() => {
        switch (currentFilter) {
            case filterOptions.NONE:
                setMaxHeight(414)
                setOverflow('scroll')
                break;
            default:
                setMaxHeight(502)
                setOverflow(null)
                break;
        }
    }, [currentFilter])

    const componentRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (componentRef.current && !componentRef.current.contains(event.target)) {
                closeHandler()
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [componentRef, closeHandler])

    return (
        <div ref={componentRef} style={{ borderRadius: '8px', boxShadow: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)', border: "1px solid #F1F1F1",  background: 'white', zIndex: '2', position: 'absolute',marginTop: "6px", overflowY: overflow, maxHeight }}>
            {currentFilter === filterOptions.DATE_ASSIGNED ? (
                <DateRangePicker
                    title="Date Assigned"
                    saveCallback={(_e, selectedDates) => {
                        dispatch(updateRangeFilter({ field: 'date_assigned', start: selectedDates.start.date, end: selectedDates.end.date }))
                        dispatch(updateLabels({ field: 'date_assigned', label: `${moment(selectedDates.start.date).format('MM/DD/YYYY')} - ${moment(selectedDates.end.date).format('MM/DD/YYYY')}` }))
                        setCurrentFilter(filterOptions.NONE)
                        dispatch(updatePendingFilters(true))
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'date_assigned' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            date_assigned: false
                        }))
                    }}
                    dateFormat='YYYY-MM-DD'
                />
            ) : currentFilter === filterOptions.LAST_CONTACTED ? (
                <DateRangePicker
                    title="Last Contacted"
                    saveCallback={(_e, selectedDates) => {
                        dispatch(updateRangeFilter({ field: 'last_contacted', start: selectedDates.start.date, end: selectedDates.end.date }))
                        dispatch(updateLabels({ field: 'last_contacted', label: `${moment(selectedDates.start.date).format('MM/DD/YYYY')} - ${moment(selectedDates.end.date).format('MM/DD/YYYY')}` }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'last_contacted' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            last_contacted: false
                        }))
                    }}
                    dateFormat='YYYY-MM-DD'
                />
            ) : currentFilter === filterOptions.LEAD_AGE ? (
                <NumberRange
                    title="Lead Age"
                    toolTip="Lead age is the persons age"
                    onSave={(start, end) => {
                        dispatch(updateRangeFilter({ field: 'lead_age', start, end }))
                        dispatch(updateLabels({ field: 'lead_age', label: `${start}-${end}` }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    onCancel={_ => {
                        dispatch(resetFilterOption({ field: 'lead_age' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            lead_age: false
                        }))
                    }}
                />
            ) : currentFilter === filterOptions.LEAD_LEVEL ? (
                <CheckBoxList
                    title="Lead Level"
                    saveCallback={(_, selectedEntries) => {
                        dispatch(updateListFilter({ field: 'lead_level', list: selectedEntries.map(entry => entry.value) }))
                        dispatch(updateLabels({ field: 'lead_level', label: selectedEntries.map(entry => entry.label).join(', ') }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'lead_level' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            lead_level: false
                        }))
                    }}
                    listData={leadLevels.map(t => ({
                        label: t.name,
                        identifier: t.name,
                        value: t.name,
                    }))}
                />
            ) : currentFilter === filterOptions.LEAD_STATUS ? (
                <CheckBoxList
                    title="Lead Status"
                    saveCallback={(_, selectedEntries) => {
                        dispatch(updateListFilter({ field: 'lead_status', list: selectedEntries.map(entry => entry.value) }))
                        dispatch(updateLabels({ field: 'lead_status', label: selectedEntries.map(entry => entry.label).join(', ') }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'lead_status' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            lead_status: false
                        }))
                    }}
                    listData={leadStatus.map(s => ({
                        label: s.name,
                        identifier: s.id,
                        value: s.id,
                    }))}
                />
            ) : currentFilter === filterOptions.LEAD_TAGS ? (
                <CheckBoxList
                    title="Lead Tags"
                    saveCallback={(_, selectedEntries) => {
                        dispatch(updateListFilter({ field: 'tags', list: selectedEntries.map(entry => entry.value) }))
                        dispatch(updateLabels({ field: 'tags', label: selectedEntries.map(entry => entry.label).join(', ') }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'tags' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            tags: false
                        }))
                    }}
                    listData={agentTags.map(t => ({
                        label: t.name,
                        identifier: t.id,
                        value: t.id,
                    }))}
                />
            ) : currentFilter === filterOptions.LEAD_TYPE ? (
                <CheckBoxList
                    title="Lead Type"
                    saveCallback={(_, selectedEntries) => {
                        dispatch(updateListFilter({ field: 'lead_type', list: selectedEntries.map(entry => entry.value) }))
                        dispatch(updateLabels({ field: 'lead_type', label: selectedEntries.map(entry => entry.label).join(', ') }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'lead_type' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            lead_type: false
                        }))
                    }}
                    listData={leadTypes.map(t => ({
                        label: t.name,
                        identifier: t.name,
                        value: t.name,
                    }))}
                />
            ) : currentFilter === filterOptions.STATE ? (
                <CheckBoxList
                    title="States"
                    saveCallback={(_, selectedEntries) => {
                         // for States, value is an array to account for state entries with multiple values.
                         // Typically this will be the state name and it's 2 letter abbreviation.
                        dispatch(updateListFilter({ field: 'state', list: selectedEntries.map(entry => entry.value).flat() }))
                        dispatch(updateLabels({ field: 'state', label: selectedEntries.map(entry => entry.label).join(', ') }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    cancelCallback={(_, __) => {
                        dispatch(resetFilterOption({ field: 'state' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            state: false
                        }))
                    }}
                    listData={stateListOptions}
                />
            ) : currentFilter === filterOptions.AMOUNT_CONTACTED ? (
                <NumberRange
                    initial_minVal={0}
                    initial_maxVal={99}
                    title="Total Dials"
                    onSave={(start, end) => {
                        dispatch(updateRangeFilter({ field: 'amount_contacted', start, end }))
                        dispatch(updateLabels({ field: 'amount_contacted', label: `${start}-${end}` }))
                        dispatch(updatePendingFilters(true))
                        setCurrentFilter(filterOptions.NONE)
                    }}
                    onCancel={_ => {
                        dispatch(resetFilterOption({ field: 'amount_contacted' }))
                        setCurrentFilter(filterOptions.NONE)

                        setSelectedFilters(prev => ({
                            ...prev,
                            amount_contacted: false
                        }))
                    }}
                />
            ) : (
                <div style={{ background: 'white', width: '100%', height: '100%', paddingTop: 6, paddingBottom: 6, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'inline-flex' }}>
                    <MenuItem iconClassName="calendar-date-selection" onClick={_ => handleFilterOption('date_assigned')} text="Date Assigned">
                        {selectedFilters.date_assigned && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="user" onClick={_ => handleFilterOption('lead_age')} text="Lead Age">
                        {selectedFilters.lead_age && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="location-pin" onClick={_ => handleFilterOption('state')} text="State">
                        {selectedFilters.state && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="clock-rewind" onClick={_ => handleFilterOption('last_contacted')} text="Last Contacted">
                        {selectedFilters.last_contacted && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="never-contacted" onClick={_ => handleFilterOption('never_contacted')} text="Never Contacted">
                        {selectedFilters.never_contacted && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="message-plus-circle" onClick={_ => handleFilterOption('amount_contacted')} text="Total Dials">
                        {selectedFilters.amount_contacted && !(amount_contacted.start === 0 && amount_contacted.end === 0) && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="tag" onClick={_ => handleFilterOption('lead_tags')} text="Lead Tags">
                        {selectedFilters.lead_tags && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="file-text" onClick={_ => handleFilterOption('lead_type')} text="Lead Type">
                        {selectedFilters.lead_type && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="file-chart" onClick={_ => handleFilterOption('lead_level')} text="Lead Level">
                        {selectedFilters.lead_level && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                    <MenuItem iconClassName="check-done" onClick={_ => handleFilterOption('lead_status')} text="Lead Status">
                        {selectedFilters.lead_status && (
                            <IndicatorDot/>
                        )}
                    </MenuItem>
                </div>
            )}
        </div>
    )
}

function IndicatorDot(props) {
    return (
        <div style={{ width: 12, height: 12, position: 'relative', marginRight: "12px" }} {...props}>
            <div style={{ width: 10, height: 10, left: 1, top: 1, position: 'absolute', background: 'var(--Colors-Foreground-fg-success-primary, #079455)', borderRadius: 9999 }} />
        </div>
    )
}

function MenuItem (props) {
    const {
        children,
        iconClassName,
        text,
        ...rest
    } = props;
    return (
        <div style={{ cursor: 'pointer', width: 320, justifyContent: 'flex-start', alignItems: 'center', display: 'inline-flex' }} {...rest}>
            <div style={{ flex: '1 1 0', minHeight: 44, overflow: 'hidden', borderRadius: 6, justifyContent: 'center', alignItems: 'center', gap: 12, display: 'flex' }}>
                <div className={iconClassName} style={{ width: 20, height: 20, position: 'relative', marginLeft: "12px" }}></div>
                <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 12, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: 'var(--Colors-Foreground-fg-primary-(900), #2F2F2F)', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>{text}</div>
                </div>
                {children}
            </div>
        </div>
    );
}
export default FilterSelector